import { useState, useEffect, useCallback } from 'react';
import { message } from 'antd';
import { getGeneralSettings, patchPinnedProjects } from '@/services/settings.service';
import {
    STORAGE_DATA_KEY,
    STORAGE_IDS_KEY,
    PINNED_PROJECTS_CHANGED_EVENT,
    readStoredPinnedProjects,
    persistPinnedProjects,
    clearPinnedProjects,
    mergePinnedMetadata,
    hasPinnedMetadataChanged,
} from '@utils/pinnedProjectsStorage';

export { persistPinnedProjects, clearPinnedProjects } from '@utils/pinnedProjectsStorage';

/**
 * Shared hook to manage pinned projects.
 * - On mount: loads pinned project IDs from the server (GET /settings/general)
 *   and seeds localStorage with the server data.
 * - togglePin(project): optimistically updates local state and localStorage,
 *   then syncs to the server via PATCH /settings/general.
 * - Dispatches 'pinnedProjectsChanged' window event so all listeners stay in sync.
 */
const usePinnedProjects = (projectsList = []) => {
    const [pinnedIds, setPinnedIds] = useState(() => readStoredPinnedProjects().ids);
    const [pinnedProjects, setPinnedProjects] = useState(() => readStoredPinnedProjects().projects);

    // On mount: load from server and seed localStorage
    useEffect(() => {
        let cancelled = false;
        getGeneralSettings()
            .then((settings) => {
                if (cancelled) return;
                const serverIds = settings?.pinnedProjectIds ?? [];
                setPinnedIds(serverIds);
                localStorage.setItem(STORAGE_IDS_KEY, JSON.stringify(serverIds));

                if (projectsList.length > 0) {
                    const pinnedMeta = projectsList
                        .filter((p) => serverIds.includes(p.id))
                        .map((p) => ({ id: p.id, name: p.name }));
                    localStorage.setItem(STORAGE_DATA_KEY, JSON.stringify(pinnedMeta));
                    setPinnedProjects(pinnedMeta);
                } else {
                    const storedProjects = readStoredPinnedProjects().projects;
                    setPinnedProjects(storedProjects);
                }

                window.dispatchEvent(new Event(PINNED_PROJECTS_CHANGED_EVENT));
            })
            .catch(() => {
                // Silently fall back to localStorage
            });
        return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (!projectsList.length || !pinnedIds.length) return;

        const merged = mergePinnedMetadata(projectsList, pinnedIds);
        if (!merged.length) return;

        const current = readStoredPinnedProjects().projects;
        if (!hasPinnedMetadataChanged(merged, current)) return;

        localStorage.setItem(STORAGE_DATA_KEY, JSON.stringify(merged));
        setPinnedProjects(merged);
        window.dispatchEvent(new Event(PINNED_PROJECTS_CHANGED_EVENT));
    }, [projectsList, pinnedIds]);

    // Listen to cross-component changes
    useEffect(() => {
        const onChanged = () => {
            const stored = readStoredPinnedProjects();
            setPinnedIds(stored.ids);
            setPinnedProjects(stored.projects);
        };
        window.addEventListener(PINNED_PROJECTS_CHANGED_EVENT, onChanged);
        return () => window.removeEventListener(PINNED_PROJECTS_CHANGED_EVENT, onChanged);
    }, []);

    const togglePin = useCallback((project) => {
        const projectId = project.id;
        const isPinned = (() => {
            try {
                const ids = JSON.parse(localStorage.getItem(STORAGE_IDS_KEY) || '[]');
                return ids.includes(projectId);
            } catch {
                return false;
            }
        })();

        const updated = isPinned
            ? pinnedIds.filter((id) => id !== projectId)
            : [...pinnedIds, projectId];

        // Optimistic local update
        setPinnedIds(updated);
        localStorage.setItem(STORAGE_IDS_KEY, JSON.stringify(updated));

        // Update metadata list
        let pinnedMeta = [];
        try {
            pinnedMeta = JSON.parse(localStorage.getItem(STORAGE_DATA_KEY) || '[]');
        } catch {
            pinnedMeta = [];
        }
        if (isPinned) {
            pinnedMeta = pinnedMeta.filter((p) => p.id !== projectId);
        } else {
            pinnedMeta.push({ id: projectId, name: project.name });
        }
        localStorage.setItem(STORAGE_DATA_KEY, JSON.stringify(pinnedMeta));
        setPinnedProjects(pinnedMeta);

        window.dispatchEvent(new Event(PINNED_PROJECTS_CHANGED_EVENT));
        message.success(isPinned ? 'Project unpinned' : 'Project pinned to sidebar');

        // Persist to server
        patchPinnedProjects(updated).catch(() => {
            // Silently fail — local state is already updated
        });
    }, [pinnedIds]);

    return { pinnedIds, pinnedProjects, togglePin };
};

export default usePinnedProjects;
